import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../services/api";

const priorities = ["Low", "Medium", "High", "Urgent"];

const ServiceRequestForm = ({
  serviceType,
  title,
  cancelTo = "/store",
  cancelState,
}) => {
  const navigate = useNavigate();

  const issueCategories = useMemo(
    () => {
      // Electrical/Plumbing need different wording + branching.
      if (serviceType === "ElectricalService") {
        return [
          "Electrical problem",
          "Not working",
          "New parts required",
          "General electrical service",
          "AMC / Annual maintenance",
        ];
      }

      if (serviceType === "PlumbingService") {
        return [
          "Plumbing problem",
          "Not working",
          "New parts required",
          "General plumbing service",
          "AMC / Annual maintenance",
        ];
      }

      // Default for IT services
      return [
        "Software issue",
        "Hardware issue",
        "Not working",
        "New parts required",
        "General service",
        "AMC / Annual maintenance",
      ];
    },
    [serviceType]
  );

  // HardwareSubOptions is used for hardware/service-specific add-ons.
  // Electrical/Plumbing use their own (non-hardware) models to keep the flow relevant.
  const hardwareSubOptions = useMemo(() => {
    if (serviceType === "LaptopService") {
      return [
        "RAM upgrade",
        "Hard disk/SSD upgrade",
        "Battery replacement",
        "Keyboard/Touchpad issue",
        "Overheating / Cooling",
        "Screen issue",
        "General hardware repair",
      ];
    }

    if (serviceType === "DesktopService") {
      return [
        "RAM upgrade",
        "Hard disk/SSD upgrade",
        "SMPS/Power issue",
        "Motherboard issue",
        "Graphics card issue",
        "Cabinet/Fan issue",
        "General hardware repair",
      ];
    }

    if (serviceType === "PrinterService") {
      return [
        "Cartridge/Ink issue",
        "Paper jam",
        "Not printing",
        "Head/Cleaning required",
        "Connectivity issue (WiFi/USB)",
        "General hardware repair",
      ];
    }

    if (serviceType === "CCTVService") {
      return [
        "Camera not working",
        "DVR/NVR issue",
        "No signal",
        "Connectivity issue",
        "Installation / repositioning",
        "General hardware repair",
      ];
    }

    if (serviceType === "ElectricalService") {
      // Keep electrical options relevant (not generic hardware upgrades)
      return [
        "Switch/Socket issue",
        "Light/Fan not working",
        "MCB/Fuse issue",
        "Wiring troubleshooting",
        "Appliance power issue",
        "General electrical repair",
      ];
    }

    if (serviceType === "PlumbingService") {
      return [
        "Leakage",
        "Tap issue",
        "Toilet flush issue",
        "Pipe block / clog",
        "Water heater issue",
        "Drainage issue",
        "General plumbing repair",
      ];
    }

    return ["General hardware repair"];
  }, [serviceType]);

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    model: "",
    priority: "",
    issueCategory: "",
    hardwareOption: "",
    message: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      // If issue category changes away from hardware/parts-related, clear hardwareOption
      if (name === "issueCategory") {
        const isPartsFlow =
          value === "Hardware issue" ||
          value === "New parts required" ||
          value === "Electrical problem" ||
          value === "Plumbing problem";

        return {
          ...prev,
          issueCategory: value,
          hardwareOption: isPartsFlow ? prev.hardwareOption : "",
        };
      }
      return { ...prev, [name]: value };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Append the extra form selections into `message`
      const extra = [];
      if (formData.issueCategory) extra.push(`Issue: ${formData.issueCategory}`);
      if (formData.hardwareOption) {
        const label =
          serviceType === "ElectricalService" || serviceType === "PlumbingService"
            ? "Service option"
            : "Hardware";
        extra.push(`${label}: ${formData.hardwareOption}`);
      }

      const finalMessage = [
        ...extra,
        formData.message ? `Details: ${formData.message}` : null,
      ]
        .filter(Boolean)
        .join("\n");

      await api.post(
        "/service-requests",
        {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          model: formData.model,
          serviceType,
          priority: formData.priority,
          message: finalMessage,
        },
        { headers: { "Content-Type": "application/json" } }
      );

      alert("Service request submitted successfully!");
      setFormData({
        name: "",
        email: "",
        phone: "",
        priority: "",
        issueCategory: "",
        hardwareOption: "",
        message: "",
      });
    } catch (error) {
      console.error(error);
      alert("Failed to submit service request");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (cancelState) {
      navigate(cancelTo, { state: cancelState });
      return;
    }
    navigate(cancelTo);
  };

  const serviceLabel = serviceType
    ? serviceType.replace("Service", " Service")
    : "Service";

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-xl bg-white shadow-lg rounded-xl p-8 relative">
        <button
          onClick={handleCancel}
          className="absolute top-4 right-4 text-gray-500 hover:text-red-500 font-medium"
        >
          Cancel
        </button>

        <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">
          {title || serviceLabel} Request
        </h2>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <input
              name="name"
              placeholder="Full Name"
              value={formData.name}
              onChange={handleChange}
              required
              className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-purple-200 focus:border-purple-500"
            />

            <input
              type="email"
              name="email"
              placeholder="Email Address"
              value={formData.email}
              onChange={handleChange}
              required
              className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-purple-200 focus:border-purple-500"
            />

            <input
              name="phone"
              placeholder="Phone Number"
              value={formData.phone}
              onChange={handleChange}
              required
              className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-purple-200 focus:border-purple-500 sm:col-span-2"
            />

            <input
              name="model"
              placeholder="Model (optional)"
              value={formData.model}
              onChange={handleChange}
              className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-purple-200 focus:border-purple-500 sm:col-span-2"
            />
          </div>

          <input
            value={serviceLabel}
            disabled
            className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm bg-slate-50 text-slate-600"
          />

          {/* Conditional issue flow */}
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-2">
              What type of issue do you have?
            </label>
            <select
              name="issueCategory"
              value={formData.issueCategory}
              onChange={handleChange}
              required
              className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-purple-200 focus:border-purple-500"
            >
              <option value="">Select issue type</option>
              {issueCategories.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>

          {(formData.issueCategory === "Hardware issue" ||
            formData.issueCategory === "New parts required" ||
            formData.issueCategory === "Electrical problem" ||
            formData.issueCategory === "Plumbing problem") && (
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-2">
                Select option
              </label>
              <select
                name="hardwareOption"
                value={formData.hardwareOption}
                onChange={handleChange}
                required
                className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-purple-200 focus:border-purple-500"
              >
                <option value="">Select option</option>
                {hardwareSubOptions.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-2">
              Priority
            </label>
            <select
              name="priority"
              value={formData.priority}
              onChange={handleChange}
              required
              className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-purple-200 focus:border-purple-500"
            >
              <option value="">Select Priority</option>
              {priorities.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </div>

          <textarea
            name="message"
            placeholder="Add details (what is not working, since when, any errors, etc.)"
            value={formData.message}
            onChange={handleChange}
            rows={4}
            className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-purple-200 focus:border-purple-500"
          />

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-purple-600 hover:bg-purple-700 text-white py-2.5 rounded-xl font-semibold transition disabled:opacity-60"
          >
            {loading ? "Submitting..." : "Submit Request"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ServiceRequestForm;

